// 依赖：axios页面服务已集成，无需额外引入
import axios from 'axios';

// 公共配置（可根据页面服务需求改为全局变量/配置文件）
const CONFIG = {
  ipv6GenServiceUrl: 'http://82.157.114.238:25870/api/gen-ipv6' // IPv6生成服务地址
};

/**
 * 构建本地服务URL并验证IP地址格式
 * @param {Object} taskUrlIp - 包含IP地址的对象，访问方式为 taskUrlIp.value
 * @returns {string} 完整的本地服务URL
 */
const buildLocalServiceUrl = (taskUrlIp) => {
  // 验证IP地址格式
  return `http://${taskUrlIp}:25871/`;
};

/**
 * 通用的本地服务POST请求函数
 * @param {Object} taskUrlIp - 包含IP地址的对象
 * @param {Object} data - 请求数据
 * @returns {Promise<Object|boolean>} 成功返回响应数据，失败返回false
 */
const postToLocalService = async (taskUrlIp, data) => {
  try {
    const url = buildLocalServiceUrl(taskUrlIp);
    console.info(`访问本地服务: ${url}`);
    const response = await axios.post(
      url,
      data,
      { 
        headers: { 'Content-Type': 'application/json' },
        timeout: 5000
      }
    );
    
    return response;
  } catch (err) {
    console.error(`本地服务请求失败: ${err.message || '网络错误'}`, err);
    return false;
  }
};

/**
 * 步骤5：发送登录成功消息
 * @param {Object} taskUrlIp - 包含IP地址的对象
 * @returns {boolean} 成功true/失败false
 */
const sendLoginSuccess = async (taskUrlIp) => {
  try {
    const response = await postToLocalService(taskUrlIp, { msg: '登录成功', code: 200 });
    
    if (response === false) {
      return false;
    }
    
    // 校验服务响应状态（必须code=200才视为成功）
    if (response.data?.code !== 200) {
      console.error('登录成功响应异常：', response.data);
      return false;
    }
    console.log('步骤5-登录成功消息发送成功:', response.data);
    return true;
  } catch (err) {
    const errMsg = `步骤5-登录成功消息发送失败：${err.message || '网络错误，请检查本地服务(25871)是否正常'}`;
    console.error(errMsg, err);
    return false;
  }
};

/**
 * 步骤6：主动获取设备信息
 * @param {Object} taskUrlIp - 包含IP地址的对象
 * @returns {object|false} 成功返回{client_info: 设备信息}，失败返回false
 */
const sendGetDeviceInfo = async (taskUrlIp) => {
  try {
    const response = await postToLocalService(taskUrlIp, { msg: '获取设备信息', code: 201 });
    
    if (response === false) {
      return false;
    }
    
    // 双重校验：状态码+设备信息存在
    if (response.data?.code !== 200 || !response.data?.client_info) {
      console.error('获取设备信息响应异常：', response.data);
      return false;
    }
    console.log('步骤6-获取设备信息成功:', response.data.client_info);
    return { client_info: response.data.client_info };
  } catch (err) {
    const errMsg = `步骤6-获取设备信息失败：${err.message || '网络错误，请检查本地服务(25871)是否正常'}`;
    console.error(errMsg, err);
    return false;
  }
};

/**
 * 步骤7：上传设备信息到IPv6生成服务，获取ipv6地址和有效期
 * @param {string} userId - 页面服务传入的用户ID
 * @param {object} clientInfo - 步骤6获取的设备信息（含ipv6前缀、ipv4地址）
 * @returns {object|false} 成功返回{ipv6: 地址, expire: 有效期}，失败返回false
 */
const uploadDeviceInfoAndGenIpv6 = async (userId, clientInfo, taskUrlIp) => {
  try {
    // 校验入参，避免无效请求
    if (!userId || !clientInfo) {
      console.error('步骤7-参数异常：用户ID或设备信息为空');
      return false;
    }
    const response = await axios.post(
      CONFIG.ipv6GenServiceUrl,
      { user_id: userId, client_info: clientInfo, client_ipv6: taskUrlIp }, // 按生成服务要求传参
      { 
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000 // 生成ipv6可能耗时，超时适当延长
      }
    );
    // 校验生成服务响应：必须包含ipv6和有效期
    if (response.data?.code !== 200 || !response.data?.ipv6 || !response.data?.expire) {
      console.error('IPv6生成服务响应异常：', response.data);
      return false;
    }
    console.log('步骤7-IPv6地址生成成功:', { ipv6: response.data.ipv6, 有效期: response.data.expire });
    return {
      ipv6: response.data.ipv6,
      expire: response.data.expire
    };
  } catch (err) {
    const errMsg = `步骤7-上传设备信息/生成IPv6失败：${err.message || '网络错误，请检查IPv6生成服务是否正常'}`;
    console.error(errMsg, err);
    return false;
  }
};

/**
 * 步骤8：给设备下发IPv6地址和有效期
 * @param {string} ipv6 - 步骤7获取的ipv6地址
 * @param {string|number} expire - 步骤7获取的有效期（支持字符串/数字，如"24h"、86400）
 * @param {Object} taskUrlIp - 包含IP地址的对象
 * @returns {boolean} 成功true/失败false
 */
const sendAssignIpv6Address = async (ipv6, expire, taskUrlIp) => {
  try {
    // 校验入参
    if (!ipv6 || !expire) {
      console.error('步骤8-参数异常：IPv6地址或有效期为空');
      return false;
    }
    const response = await postToLocalService(taskUrlIp, { 
      msg: '下发ipv6地址', 
      code: 202, 
      ipv6, 
      expire 
    });
    
    if (response === false) {
      return false;
    }
    
    if (response.data?.code !== 200) {
      console.error('下发IPv6响应异常：', response.data);
      return false;
    }
    console.log('步骤8-IPv6地址下发成功:', response.data);
    return true;
  } catch (err) {
    const errMsg = `步骤8-下发IPv6地址失败：${err.message || '网络错误，请检查本地服务(25871)是否正常'}`;
    console.error(errMsg, err);
    return false;
  }
};

/**
 * 主入口函数：顺序执行5-8步，页面服务登录后调用此函数，传入用户ID
 * @param {string} userId - 页面服务传入的用户ID（核心参数）
 * @param {Object} taskUrlIp - 包含IP地址的对象
 * @returns {boolean} 全部步骤成功返回true，任意一步失败返回false
 */
const executeIpv6Flow = async (userId, taskUrlIp) => {
  console.log('开始执行IPv6流程，用户ID：', userId);
  
  try {
    // 步骤5：登录成功消息 - 失败则终止
    const step5Ok = await sendLoginSuccess(taskUrlIp);
    if (!step5Ok) return false;

    // 步骤6：获取设备信息 - 失败则终止，成功返回设备信息
    const step6Res = await sendGetDeviceInfo(taskUrlIp);
    if (!step6Res) return false;
    const { client_info } = step6Res;

    // 步骤7：上传信息生成IPv6 - 失败则终止，成功返回ipv6和有效期
    const step7Res = await uploadDeviceInfoAndGenIpv6(userId, client_info, taskUrlIp);
    if (!step7Res) return false;
    const { ipv6, expire } = step7Res;

    // 步骤8：下发IPv6地址 - 最终步骤
    const step8Ok = await sendAssignIpv6Address(ipv6, expire, taskUrlIp);
    if (!step8Ok) return false;

    // 所有步骤成功
    console.log('IPv6地址生成及下发流程全部完成！');
    return true;
  } catch (err) {
    // 全局异常兜底
    console.error('IPv6流程全局异常：', err);
    return false;
  }
};

// 暴露主入口函数，供页面服务前端调用
export {
  executeIpv6Flow,
  sendLoginSuccess,
  sendGetDeviceInfo,
  uploadDeviceInfoAndGenIpv6,
  sendAssignIpv6Address
};
// 若页面服务不支持ES6 export，可改为全局变量
// window.executeIpv6Flow = executeIpv6Flow;